import { useState } from 'react';
import { Alert, Empty, Flex } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Icon from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { Button } from '@/components/button/button';
import FilterChip from '@/components/filter-chip/filter-chip';
import Meter from '@/components/meter/meter';
import StatusBadge from '@/components/status-badge/status-badge';
import VsTable from '@/components/vs-table/vs-table';
import { useOutboundCampaigns } from '@/hooks/use-outbound-campaigns/use-outbound-campaigns';
import { useOutboundScenarios } from '@/hooks/use-outbound-scenarios/use-outbound-scenarios';
import { OutboundCampaign } from '@/services/outbound-calls-service/campaigns.service.types';
import {
  CAMPAIGN_STATUSES,
  CampaignStatus,
} from '@/services/outbound-calls-service/outbound-calls.constants';
import { useOutboundContext } from '../outbound-calls.context';
import { campaignActivityLabel, outboundErrorMessage } from '../outbound-calls.messages';
import {
  CAMPAIGN_STATUS_BADGE,
  campaignPath,
  formatCallDays,
  formatDateTime,
  settledEntries,
  toHourMinute,
} from '../outbound-calls.utils';
import { CampaignFormModal } from './campaign-form-modal';
import styles from '../outbound-calls.module.scss';

export function OutboundCampaigns() {
  const { t } = useTranslation(['outbound', 'common']);
  const navigate = useNavigate();
  const { company } = useOutboundContext();
  const { campaigns, isLoading, error } = useOutboundCampaigns(company.id);
  const { scenarios } = useOutboundScenarios(company.id);
  const [status, setStatus] = useState<CampaignStatus | undefined>();
  const [formOpen, setFormOpen] = useState(false);

  // Not paginated and short, so the chips filter what is already here.
  const visible = status ? campaigns.filter((campaign) => campaign.status === status) : campaigns;

  const columns: ColumnsType<OutboundCampaign> = [
    {
      title: t('outbound:campaigns.columns.name'),
      key: 'name',
      render: (_, campaign) => (
        <span className={styles.stack}>
          <Link to={campaignPath(campaign.id)} onClick={(event) => event.stopPropagation()}>
            <b>{campaign.name}</b>
          </Link>
          <span className={styles.secondary}>{campaign.scenario_name}</span>
        </span>
      ),
    },
    {
      title: t('outbound:campaigns.columns.status'),
      key: 'status',
      render: (_, campaign) => (
        <StatusBadge
          variant={CAMPAIGN_STATUS_BADGE[campaign.status]}
          label={t(`outbound:campaignStatus.${campaign.status}`)}
        />
      ),
    },
    {
      title: t('outbound:campaigns.columns.progress'),
      key: 'progress',
      width: 180,
      render: (_, campaign) => {
        const { total } = campaign.entry_counts;
        const done = settledEntries(campaign.entry_counts);
        return (
          <Meter
            value={total ? (done / total) * 100 : 0}
            label={t('outbound:campaigns.progress', { done, total })}
            ariaLabel={t('outbound:campaigns.progressLabel', { name: campaign.name })}
          />
        );
      },
    },
    {
      title: t('outbound:campaigns.columns.window'),
      key: 'window',
      render: (_, campaign) =>
        t('outbound:campaigns.window', {
          start: toHourMinute(campaign.window_start),
          end: toHourMinute(campaign.window_end),
          days: formatCallDays(campaign.call_days),
        }),
    },
    {
      title: t('outbound:campaigns.columns.activity'),
      key: 'activity',
      render: (_, campaign) => (
        <span className={`${styles.secondary} ${styles.clamp}`}>
          {campaignActivityLabel(campaign.activity)}
        </span>
      ),
    },
    {
      title: t('outbound:campaigns.columns.startedAt'),
      key: 'started_at',
      render: (_, campaign) => formatDateTime(campaign.started_at),
    },
  ];

  return (
    <Flex vertical gap={14}>
      <div className={styles.toolbar}>
        <div className={styles.chips}>
          <FilterChip
            label={t('common:states.all')}
            active={!status}
            onClick={() => setStatus(undefined)}
          />
          {CAMPAIGN_STATUSES.map((value) => (
            <FilterChip
              key={value}
              label={t(`outbound:campaigns.filters.${value}`)}
              active={status === value}
              onClick={() => setStatus(value)}
            />
          ))}
        </div>
        <Button buttonVariant="primary" onClick={() => setFormOpen(true)}>
          <Icon component={() => <PlusIcon className={styles.buttonIcon} />} />
          {t('outbound:campaigns.add')}
        </Button>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={outboundErrorMessage(error, t('outbound:errors.load'))}
        />
      ) : (
        <VsTable<OutboundCampaign>
          rowKey="id"
          loading={isLoading}
          dataSource={visible}
          columns={columns}
          scroll={{ x: 900 }}
          onRow={(campaign) => ({
            onClick: () => navigate(campaignPath(campaign.id)),
            className: styles.clickable,
          })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  status ? t('outbound:campaigns.emptyFiltered') : t('outbound:campaigns.empty')
                }
              />
            ),
          }}
        />
      )}

      <CampaignFormModal
        open={formOpen}
        companyId={company.id}
        scenarios={scenarios}
        onClose={() => setFormOpen(false)}
        onSaved={(campaign) => {
          setFormOpen(false);
          navigate(campaignPath(campaign.id));
        }}
      />
    </Flex>
  );
}
